import type { Unit, Pixel } from '@/data';
import { pageToPageData } from '@/http-client/utils/pageToPageData';
import MetadataParser from '../MetadataParser';
import type { Image3DRowFormat, Image3DSyncFormat } from '../UnitParser';
import UnitParser from '../UnitParser';
import { Structure } from '../../data/Structure';
import type { StructureFormat } from './StructureFormat';

export default class StructureParser {
  private static ROW_HEIGHT = 150 as Pixel;

  private static PIXEL_BY_ONSET = 100 as Pixel;

  static structureFormatFromNewFormat(sourceFormat: Map<string, unknown>): StructureFormat {
    return {
      info: sourceFormat.get('text') as string,
      license: sourceFormat.get('license') as string,
      contributors: sourceFormat.get('contributors') as Record<string, string>,
      id: sourceFormat.get('id') as string,
      name: sourceFormat.get('name') as string,
      year: sourceFormat.get('year') as string,
    };
  }

  static fromFormat(
    format: StructureFormat,
  ): Structure {
    const name = format?.name;

    const id = format?.id;

    const positions: Image3DSyncFormat = StructureParser.getGeneratedPositions();
    const pixelUnit: Unit<Pixel> = UnitParser.createImage3DUnit(positions);

    const width: Pixel = this.calculateMaxWidthInFirstPage(positions);
    const height = StructureParser.calculateTotalHeightInFirstPage(positions);
    const pages = positions.pages.map((page) => pageToPageData(page, { height, width }));

    const metadata = MetadataParser.fromFormat(format);

    const svgImage = this.generateSvgFromRows(positions);

    return new Structure({
      name, id, pixelUnit, width, height, pages, metadata, svgImage,
    });
  }

  private static getGeneratedPositions(): Image3DSyncFormat {
    const quarterNotePerMeasure = 4;
    const nbMeasuresInStructureTestPiece = 35.5;
    const nbOnset = quarterNotePerMeasure * (nbMeasuresInStructureTestPiece - 1);
    const totalWidthInPixel = nbOnset * this.PIXEL_BY_ONSET;

    const lastXInFirstRow = 1400;
    const Y_MARGIN = 100;
    const START_X = 110;
    const STAFF_HEIGHT = 50;
    return {
      pages: [
        {
          image: '',
          rows: [
            {
              'start-x': START_X,
              'end-x': lastXInFirstRow,
              y: Y_MARGIN,
              height: this.ROW_HEIGHT,
              'onset-x': [
                { onset: 0, x: START_X },
                { onset: 8, x: 800 },
                { onset: 16, x: lastXInFirstRow },
              ],
              staves: [
                { top: Y_MARGIN, bottom: Y_MARGIN + STAFF_HEIGHT },
              ],
            },
            {
              'start-x': START_X,
              'end-x': 4000 - lastXInFirstRow,
              y: this.ROW_HEIGHT + (2 * Y_MARGIN),
              height: this.ROW_HEIGHT,
              'onset-x': [
                { onset: 16, x: START_X },
                { onset: 20, x: 2000 - lastXInFirstRow },
                { onset: 24, x: 2400 - lastXInFirstRow },
                { onset: 28, x: 2800 - lastXInFirstRow },
                { onset: 40, x: 4000 - lastXInFirstRow },
              ],
              staves: [
                {
                  top: this.ROW_HEIGHT + (2 * Y_MARGIN),
                  bottom: this.ROW_HEIGHT + (2 * Y_MARGIN) + STAFF_HEIGHT,
                },
              ],
            },
            {
              'start-x': START_X,
              'end-x': totalWidthInPixel - 4000,
              y: (this.ROW_HEIGHT * 2) + (3 * Y_MARGIN),
              height: this.ROW_HEIGHT,
              'onset-x': [
                { onset: 40, x: START_X },
                { onset: 60, x: 6000 - 4000 },
                { onset: 70, x: 7000 - 4000 },
                { onset: 85, x: 8500 - 4000 },
                { onset: 100, x: 10000 - 4000 },
                { onset: nbOnset, x: totalWidthInPixel - 4000 },
              ],
              staves: [
                {
                  top: (this.ROW_HEIGHT * 2) + (3 * Y_MARGIN),
                  bottom: (this.ROW_HEIGHT * 2) + (3 * Y_MARGIN) + STAFF_HEIGHT,
                },
              ],
            },
          ],
        },
      ],
    };
  }

  private static calculateMaxWidthInFirstPage(positions: Image3DSyncFormat): Pixel {
    const oneOnsetMargin = this.PIXEL_BY_ONSET;

    const widthMaxInRowsInFirstPage = positions.pages[0].rows
      .map((row) => row['end-x'])
      .reduce((prev, curr) => Math.max(prev, curr));

    return (widthMaxInRowsInFirstPage + oneOnsetMargin) as Pixel;
  }

  private static calculateTotalHeightInFirstPage(positions: Image3DSyncFormat) {
    const nbrRowsFirstPage = positions.pages[0].rows.length;

    return (this.ROW_HEIGHT * (nbrRowsFirstPage + 1)) as Pixel;
  }

  private static generateSvgFromRows(positions: Image3DSyncFormat) {
    const firstPage = positions.pages[0];
    const getXInTheRow = (row: Image3DRowFormat) => row['onset-x'].map((XOnset) => XOnset.x);
    const getLinesInfoToGenerateSVG = (row: Image3DRowFormat) => {
      const lastXOnsetIndex = row['onset-x'].length - 1;
      const endX = row['onset-x'][lastXOnsetIndex].x;
      return {
        cellX: getXInTheRow(row),
        y: row.y,
        endX,
      };
    };
    return firstPage.rows
      .map(getLinesInfoToGenerateSVG)
      .map((value) => this.generateLine(value.cellX, value.y, value.endX))
      .join();
  }

  private static generateLine(xList: number[], yLine:number, endX: number) {
    return xList.map((x, index, array) => {
      const nextX = array[index + 1] || endX;
      const width = nextX - x;
      if (width !== 0) {
        return StructureParser.generateCell(x, yLine, width, this.ROW_HEIGHT);
      }
      return '';
    }).join();
  }

  private static generateCell(x: number, y:number, width: number, height: number): string {
    const verticalPadding = 10;
    const xShiftToAlignWithLabels = 7;
    return `<rect x="${x - xShiftToAlignWithLabels}" y="${y - verticalPadding}" width="${width}" height="${height + 2 * verticalPadding}" class="structure"></rect>`;
  }
}
